import { clip, endPath, LineCapStyle, PDFDocument, popGraphicsState, pushGraphicsState, rectangle, rgb, StandardFonts, } from 'pdf-lib';
import { OVERLAY_FONT_SIZE, OVERLAY_PADDING, TEXT_OVERLAY_BORDER, TEXT_OVERLAY_PAD, layoutOverlayText, } from 'libs.consent.common';
import { signatureOverlayLines } from './signature_overlay_lines';
const SIGNATURE_OVERLAY_FONT_SIZE = 9;
const OVERLAY_LINE_HEIGHT = 12;
const OVERLAY_LINE_HEIGHT_RATIO = OVERLAY_LINE_HEIGHT / SIGNATURE_OVERLAY_FONT_SIZE;
/**
 * Signed-stamp palette from Figma `Fields` `State=Filled`.
 * Participant-role stamps are green (#DCFCE7 / #BBF7D0); every other
 * `signerType` is grey (#E5E7EB). Drawn opaque with a hairline border. pdf-lib
 * cannot round a rectangle, so the design's 6px corner radius is the one part a
 * baked PDF drops.
 */
const SIGNED_FILL = rgb(0.8627, 0.9882, 0.9059); // #DCFCE7
const SIGNED_BORDER = rgb(0.7333, 0.9686, 0.8157); // #BBF7D0
const OTHER_SIGNED_FILL = rgb(0.898, 0.9059, 0.9216); // #E5E7EB
const OTHER_SIGNED_BORDER = rgb(0.898, 0.9059, 0.9216); // #E5E7EB
const OVERLAY_TEXT_COLOR = rgb(0.0667, 0.0941, 0.1529); // #111827
/**
 * A stamped date reuses the live date overlay's own palette — white fill, primary border — rather
 * than the signed-signature green/grey. The signer saw a white input with a purple border, and
 * live-vs-baked agreement is the property this file is built around; a date is also not a
 * signature, so borrowing the signature stamp's "filled" colour would misrepresent it as one.
 */
/** Choice marks match the patient archive: Colors.primary at 0.9, with a white tick. */
const CHOICE_MARK_COLOR = rgb(0.3725, 0.2745, 0.8118); // #5F46CF, Colors.primary
const CHOICE_TICK_COLOR = rgb(1, 1, 1);
const CHOICE_MARK_OPACITY = 0.9;
const DATE_FILL = rgb(1, 1, 1); // #fff
const DATE_BORDER = rgb(0.3725, 0.2745, 0.8118); // #5F46CF, Colors.primary
const OVERLAY_BORDER_WIDTH = 1;
// Below this the stamp stops being legible, so past it we accept overflowing
// the box rather than dropping a line.
const OVERLAY_MIN_FONT_SIZE = 5;
function isFontEncodable(font, text) {
    try {
        font.encodeText(text);
        return true;
    }
    catch {
        return false;
    }
}
const glyphSets = new WeakMap();
/**
 * Helvetica: encodeText throws on missing WinAnsi. Noto: encodeText does not —
 * missing glyphs become an empty box — so coverage is getCharacterSet (the
 * fontkit cmap), with encodeText as the Jest-mock fallback.
 */
function fontHasGlyph(font, codePoint) {
    if (typeof font.getCharacterSet === 'function') {
        let set = glyphSets.get(font);
        if (!set) {
            set = new Set(font.getCharacterSet());
            glyphSets.set(font, set);
        }
        return set.has(codePoint);
    }
    return isFontEncodable(font, String.fromCodePoint(codePoint));
}
/**
 * Fetch Noto Sans JP when any overlay character is in this range. Hangul is
 * included so a Korean name loads JP (which still has no Hangul); the stamp
 * then throws rather than embedding a blank name. Which font draws a character
 * is glyph coverage, not this regex.
 */
const CJK_RE = /[\u1100-\u11FF\u2E80-\u9FFF\uAC00-\uD7AF\uF900-\uFAFF\uFF00-\uFFEF]/;
function overlayCharNeedsCjk(ch) {
    return CJK_RE.test(ch);
}
function overlayCharNeedsNotoSans(ch, helvetica) {
    if (ch === '\n' || ch === '\r' || overlayCharNeedsCjk(ch)) {
        return false;
    }
    return !isFontEncodable(helvetica, ch);
}
function overlayText(text) {
    return text.normalize('NFC');
}
/**
 * WinAnsi names stay on Helvetica so Latin stamps match the live overlay.
 * Once any character needs Noto, the whole string uses Noto Sans, then JP for
 * glyphs Noto lacks — never Helvetica mid-word. Mixed `Łukasz 山田` still
 * splits Noto / JP. If no loaded font has a glyph, throw.
 */
export function createStampFonts(helvetica, helveticaBold, unicode) {
    const runsFor = (text, weight) => {
        const composed = overlayText(text);
        const winAnsi = weight === 'bold' ? helveticaBold : helvetica;
        const useWinAnsi = isFontEncodable(helvetica, composed.replace(/[\r\n]/g, ''));
        const pickFont = (codePoint) => {
            if (useWinAnsi) {
                return winAnsi;
            }
            if (weight === 'bold' && unicode?.bold && fontHasGlyph(unicode.bold, codePoint)) {
                return unicode.bold;
            }
            if (unicode?.regular && fontHasGlyph(unicode.regular, codePoint)) {
                return unicode.regular;
            }
            if (unicode?.cjk && fontHasGlyph(unicode.cjk, codePoint)) {
                return unicode.cjk;
            }
            throw new Error(`No overlay font has a glyph for "${String.fromCodePoint(codePoint)}"`);
        };
        const runs = [];
        for (const ch of composed) {
            if (ch === '\n' || ch === '\r') {
                continue;
            }
            const font = pickFont(ch.codePointAt(0));
            const last = runs[runs.length - 1];
            if (last?.font === font) {
                last.text += ch;
            }
            else {
                runs.push({ text: ch, font });
            }
        }
        return runs;
    };
    return { regular: helvetica, bold: helveticaBold, runsFor };
}
export function widthOfOverlayRuns(runs, size) {
    return runs.reduce((sum, run) => sum + run.font.widthOfTextAtSize(run.text, size), 0);
}
function drawOverlayRuns(page, runs, x, y, size) {
    let cursor = x;
    for (const run of runs) {
        page.drawText(run.text, {
            x: cursor,
            y,
            size,
            font: run.font,
            color: OVERLAY_TEXT_COLOR,
        });
        cursor += run.font.widthOfTextAtSize(run.text, size);
    }
    return cursor;
}
/**
 * Largest font size that fits every manifestation line inside the signature
 * box, horizontally and vertically.
 *
 * The 2-line stamp still overflows a box authored for a short unsigned tab, so
 * we scale rather than drop the date/id. Measuring line 0 as bold name +
 * regular rest keeps fit honest with the split draw.
 */
export function fitOverlayFontSize(lines, font, usableWidth, usableHeight, widthOfLine) {
    if (lines.length === 0) {
        return SIGNATURE_OVERLAY_FONT_SIZE;
    }
    let size = SIGNATURE_OVERLAY_FONT_SIZE;
    const byHeight = usableHeight / (lines.length * OVERLAY_LINE_HEIGHT_RATIO);
    if (byHeight < size) {
        size = byHeight;
    }
    const measure = (line, index, at) => widthOfLine ? widthOfLine(line, index, at) : font.widthOfTextAtSize(line, at);
    // Text width is linear in font size for a given string, so one measurement
    // at the base size is enough to solve for the size that fits.
    const widestAtBase = Math.max(...lines.map((line, index) => measure(line, index, SIGNATURE_OVERLAY_FONT_SIZE)));
    if (widestAtBase > 0 && usableWidth > 0) {
        const byWidth = SIGNATURE_OVERLAY_FONT_SIZE * (usableWidth / widestAtBase);
        if (byWidth < size) {
            size = byWidth;
        }
    }
    return Math.max(size, OVERLAY_MIN_FONT_SIZE);
}
/**
 * Left origin that centres one line inside the stamp - pdf-lib has no
 * text-align, so the caller computes it.
 *
 * Centred on the *fill*, not the authored box: {@link overlayFillSize} grows
 * the backdrop when the text overflows, and the fill is what a reader
 * perceives as the stamp. Clamped at the padding so a line wider than the fill
 * still starts inside it rather than at a negative offset off the page.
 *
 * The live overlay centres via CSS (`text-align: center`) and the patient app
 * baker mirrors this function, so all three surfaces place the same legal
 * record the same way.
 */
export function overlayLineX(lineWidth, fillWidth, boxX) {
    return boxX + Math.max(OVERLAY_PADDING, (fillWidth - lineWidth) / 2);
}
/**
 * Baseline that optically centres a single line in the fill, for stamps authored in a box roughly
 * their own height — the signature stamp's top-down flow would sit such a line high.
 *
 * `0.15` approximates half of Helvetica's cap height. Exported for the same reason as
 * {@link overlayLineX}: the patient app baker has to place the line identically.
 */
export function overlayCenteredBaselineY(boxTopY, fillHeight, fontSize) {
    return boxTopY - fillHeight / 2 - fontSize / 2 + fontSize * 0.15;
}
/**
 * Size of the overlay backdrop: the text extent, never smaller than the
 * authored box.
 *
 * `fitOverlayFontSize` stops scaling at OVERLAY_MIN_FONT_SIZE and then lets the
 * stamp overflow rather than drop an audit line, and a narrow box overflows
 * every time - a box under ~32% wide cannot hold a 24-character record id
 * at any legible size. Filling only the authored box left that overflow
 * sitting on bare page content, so the tail of the date and id was unreadable
 * against whatever the consent said underneath.
 */
export function overlayFillSize(lines, font, fontSize, lineHeight, boxWidth, boxHeight, widthOfLine) {
    const measure = (line, index) => widthOfLine ? widthOfLine(line, index, fontSize) : font.widthOfTextAtSize(line, fontSize);
    const textWidth = lines.length ? Math.max(...lines.map((line, index) => measure(line, index))) : 0;
    return {
        width: Math.max(boxWidth, textWidth + OVERLAY_PADDING * 2),
        height: Math.max(boxHeight, lines.length * lineHeight + OVERLAY_PADDING * 2),
    };
}
function drawSignatureOverlay(page, field, fonts, locale) {
    const { width: pageWidth, height: pageHeight } = page.getSize();
    const x = (field.xPct / 100) * pageWidth;
    const y = pageHeight - (field.yPct / 100) * pageHeight;
    const boxWidth = (field.widthPct / 100) * pageWidth;
    const boxHeight = (field.heightPct / 100) * pageHeight;
    const lines = signatureOverlayLines(field, locale).map(overlayText);
    const signedBy = overlayText((field.name || '').trim());
    const layouts = lines.map((line, index) => {
        const remainder = index === 0 && signedBy && line.startsWith(signedBy) && line.length > signedBy.length
            ? line.slice(signedBy.length)
            : '';
        if (remainder) {
            return { nameRuns: fonts.runsFor(signedBy, 'bold'), restRuns: fonts.runsFor(remainder, 'regular') };
        }
        return {
            nameRuns: fonts.runsFor(line, index === 0 ? 'bold' : 'regular'),
            restRuns: [],
        };
    });
    const widthOfLine = (_line, index, size) => {
        const layout = layouts[index];
        return widthOfOverlayRuns(layout.nameRuns, size) + widthOfOverlayRuns(layout.restRuns, size);
    };
    const fontSize = fitOverlayFontSize(lines, fonts.regular, boxWidth - OVERLAY_PADDING * 2, boxHeight - OVERLAY_PADDING * 2, widthOfLine);
    const lineHeight = fontSize * OVERLAY_LINE_HEIGHT_RATIO;
    const { width: fillWidth, height: fillHeight } = overlayFillSize(lines, fonts.regular, fontSize, lineHeight, boxWidth, boxHeight, widthOfLine);
    const fill = field.otherSigner ? OTHER_SIGNED_FILL : SIGNED_FILL;
    const border = field.otherSigner ? OTHER_SIGNED_BORDER : SIGNED_BORDER;
    page.drawRectangle({
        x,
        y: y - fillHeight,
        width: fillWidth,
        height: fillHeight,
        color: fill,
        borderColor: border,
        borderWidth: OVERLAY_BORDER_WIDTH,
    });
    let textY = y - OVERLAY_PADDING - fontSize;
    layouts.forEach((layout) => {
        const lineWidth = widthOfOverlayRuns(layout.nameRuns, fontSize) + widthOfOverlayRuns(layout.restRuns, fontSize);
        const lineX = overlayLineX(lineWidth, fillWidth, x);
        const nameEnd = drawOverlayRuns(page, layout.nameRuns, lineX, textY, fontSize);
        if (layout.restRuns.length > 0) {
            drawOverlayRuns(page, layout.restRuns, nameEnd, textY, fontSize);
        }
        textY -= lineHeight;
    });
}
/**
 * Renders an entered date for the archived record.
 *
 * Deliberately a month *name* in the document's locale (`dateStyle: 'medium'`), not the numeric
 * form the signer typed into. The signer entered the date through a native date field in their own
 * browser's format, so `03/04/2026` on screen is `3 April` to one reader and `March 4` to another.
 * A numeric stamp would carry that ambiguity onto the permanent record, where nobody can ask which
 * browser produced it. A month name cannot be misread, and it matches the `dateStyle: 'medium'`
 * the signature stamp on the same page already uses.
 *
 * `timeZone: 'UTC'` because this is a plain calendar date with no zone of its own — formatting it
 * in a local zone can shift it a day.
 */
export function formatEnteredDate(isoValue, locale) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(isoValue)) {
        return '';
    }
    // `2026-02-31` is shaped like a date but is not one, and `Date` rolls it forward to 3 March
    // instead of refusing it — a stamp built from that would show a date nobody entered. Round-trip
    // the parse and keep the stored text when it does not survive.
    const parsed = new Date(`${isoValue}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== isoValue) {
        return isoValue;
    }
    try {
        return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(parsed);
    }
    catch {
        // An unusable locale tag must not cost the record its date.
        return isoValue;
    }
}
/**
 * The standard fonts this baker embeds are WinAnsi-encoded, and a localized date carries characters
 * WinAnsi has no room for — `4 paź 2026`, `2026年10月4日`, `4 окт. 2026 г.` all make pdf-lib throw.
 * That throw would abort the whole bake, so a locale whose date cannot be drawn falls back to the
 * stored ISO value: still unambiguous about which day was meant, which is the reason a month name
 * was preferred in the first place, and encodable in every font.
 *
 * Exported for tests.
 */
export function encodableOrIso(preferred, isoValue, font) {
    try {
        font.encodeText(preferred);
        return preferred;
    }
    catch {
        return isoValue.trim();
    }
}
function drawEnteredDateOverlay(page, field, font, locale) {
    // A value that reached here was answered, so it gets stamped even if it cannot be formatted:
    // the raw text on the record beats a box that looks like a field nobody placed.
    const text = encodableOrIso(formatEnteredDate(field.value, locale) || field.value.trim(), field.value, font);
    if (!text) {
        return;
    }
    const { width: pageWidth, height: pageHeight } = page.getSize();
    const x = (field.xPct / 100) * pageWidth;
    const y = pageHeight - (field.yPct / 100) * pageHeight;
    const boxWidth = (field.widthPct / 100) * pageWidth;
    const boxHeight = (field.heightPct / 100) * pageHeight;
    const lines = [text];
    // Shares the signature stamp's fit and fill terms on purpose: the two stamps sit on the same
    // page, and a date that shrank or grew on different rules would not line up with them.
    const fontSize = fitOverlayFontSize(lines, font, boxWidth - OVERLAY_PADDING * 2, boxHeight - OVERLAY_PADDING * 2);
    const lineHeight = fontSize * OVERLAY_LINE_HEIGHT_RATIO;
    const { width: fillWidth, height: fillHeight } = overlayFillSize(lines, font, fontSize, lineHeight, boxWidth, boxHeight);
    page.drawRectangle({
        x,
        y: y - fillHeight,
        width: fillWidth,
        height: fillHeight,
        color: DATE_FILL,
        borderColor: DATE_BORDER,
        borderWidth: OVERLAY_BORDER_WIDTH,
    });
    page.drawText(text, {
        // Centred like the signature stamp beside it, rather than flush left like the live date input:
        // the two stamps share a page in the archived record, and the record is what has to look
        // deliberate. The live field is a native control whose alignment the browser owns.
        x: overlayLineX(font.widthOfTextAtSize(text, fontSize), fillWidth, x),
        y: overlayCenteredBaselineY(y, fillHeight, fontSize),
        size: fontSize,
        font,
        color: OVERLAY_TEXT_COLOR,
    });
}
/** Drawn in place of a character Helvetica (WinAnsi) has no glyph for. */
const UNENCODABLE_CHARACTER_STAND_IN = '?';
/**
 * Validation keeps new answers WinAnsi-only, but a value saved before that rule (or by another
 * client) can still hold characters Helvetica cannot draw. pdf-lib would throw, and one throw aborts
 * the whole final document, so those characters are drawn as `?` instead. The warning names the
 * field, never the value, because the value is the participant's answer.
 */
function encodableOverlayText(field, font) {
    // WinAnsi has no glyph for a line break, so check what is drawn: the line breaks become separate
    // `drawText` calls.
    const isEncodable = (text) => {
        try {
            font.encodeText(text.replace(/[\r\n]/g, ''));
            return true;
        }
        catch {
            return false;
        }
    };
    if (isEncodable(field.value)) {
        return field.value;
    }
    let replaced = 0;
    const text = Array.from(field.value, (ch) => {
        if (isEncodable(ch)) {
            return ch;
        }
        replaced += 1;
        return UNENCODABLE_CHARACTER_STAND_IN;
    }).join('');
    console.warn(`Free-text overlay ${field.ec__key} has ${replaced} character(s) Helvetica cannot draw; stamped as "${UNENCODABLE_CHARACTER_STAND_IN}".`);
    return text;
}
/**
 * Stamps a free-text answer in Helvetica. The live overlay is a wrapping
 * textarea that stays inside the authored box (14px, transparent fill, 2px
 * outline, first line on the bottom). The archive has to agree — same pad,
 * same line count the signer saw (largest size ≤ 14pt whose wrap fits), never
 * grow the box or paint an opaque fill over the PDF.
 *
 * Outline colour matches the live filled/other-signer borders so a completed
 * text field still reads as answered without covering page text.
 */
function drawEnteredTextOverlay(page, field, font) {
    if (!field.value) {
        return;
    }
    const text = encodableOverlayText(field, font);
    const { width: pageWidth, height: pageHeight } = page.getSize();
    const x = (field.xPct / 100) * pageWidth;
    const y = pageHeight - (field.yPct / 100) * pageHeight;
    const boxWidth = (field.widthPct / 100) * pageWidth;
    const boxHeight = (field.heightPct / 100) * pageHeight;
    const { lines, fontSize, lineHeight } = layoutOverlayText(text, boxWidth, boxHeight, (line) => font.widthOfTextAtSize(line, OVERLAY_FONT_SIZE));
    const fillHeight = Math.min(boxHeight, y);
    const border = field.otherSigner ? OTHER_SIGNED_BORDER : SIGNED_BORDER;
    page.drawRectangle({
        x,
        y: y - fillHeight,
        width: boxWidth,
        height: fillHeight,
        borderColor: border,
        borderWidth: TEXT_OVERLAY_BORDER,
    });
    if (lines.length === 0) {
        return;
    }
    // Text never paints past the authored box, even a value too long for it at the minimum size.
    page.pushOperators(pushGraphicsState(), rectangle(x, y - fillHeight, boxWidth, fillHeight), clip(), endPath());
    // The live field is border-box, so its text starts inside both the outline and the padding.
    const inset = TEXT_OVERLAY_PAD + TEXT_OVERLAY_BORDER;
    const lastBaseline = y - fillHeight + inset;
    let baseline = lastBaseline + (lines.length - 1) * lineHeight;
    const topMax = y - inset - fontSize;
    if (baseline > topMax) {
        baseline = topMax;
    }
    for (const line of lines) {
        page.drawText(line, {
            x: x + inset,
            y: baseline,
            size: fontSize,
            font,
            color: OVERLAY_TEXT_COLOR,
        });
        baseline -= lineHeight;
    }
    page.pushOperators(popGraphicsState());
}
/**
 * Marks a selected option at its authored box, the way the patient archive does
 * (`axon/react-native` `studio-consent-pdf.helper`): a checkbox gets a filled square with a vector
 * tick, a radio gets a filled dot. The tick is drawn as two lines because the archive's Helvetica
 * has no tick glyph and a font-based one falls back to 'X'.
 */
function drawSelectedChoiceOverlay(page, field) {
    const { width: pageWidth, height: pageHeight } = page.getSize();
    const x = (field.xPct / 100) * pageWidth;
    const width = (field.widthPct / 100) * pageWidth;
    const height = (field.heightPct / 100) * pageHeight;
    const y = pageHeight - (field.yPct / 100) * pageHeight - height;
    if (!field.multi) {
        page.drawEllipse({
            x: x + width / 2,
            y: y + height / 2,
            xScale: width / 2,
            yScale: height / 2,
            color: CHOICE_MARK_COLOR,
            opacity: CHOICE_MARK_OPACITY,
        });
        return;
    }
    page.drawRectangle({ x, y, width, height, color: CHOICE_MARK_COLOR, opacity: CHOICE_MARK_OPACITY });
    const thickness = Math.max(1, Math.min(width, height) * 0.12);
    // Tick vertices as fractions of the box, origin bottom-left.
    const low = { x: x + width * 0.24, y: y + height * 0.52 };
    const vertex = { x: x + width * 0.42, y: y + height * 0.32 };
    const high = { x: x + width * 0.76, y: y + height * 0.72 };
    page.drawLine({ start: low, end: vertex, thickness, color: CHOICE_TICK_COLOR, lineCap: LineCapStyle.Round });
    page.drawLine({ start: vertex, end: high, thickness, color: CHOICE_TICK_COLOR, lineCap: LineCapStyle.Round });
}
/**
 * Renders signature overlay text onto the given PDF bytes using pdf-lib.
 * Each overlay is positioned at its percentage-based coordinates on the
 * target page, preserving the original PDF's vector text and accessibility.
 */
export async function generateSignedPdf({ pdfBytes, signatures, dates = [], texts = [], choices = [], locale, }) {
    const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
    const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const overlayLines = signatures.flatMap((field) => signatureOverlayLines(field, locale).map(overlayText));
    const unencodable = overlayLines.filter((line) => !isFontEncodable(helvetica, line.replace(/[\r\n]/g, '')));
    let unicode;
    if (unencodable.length > 0) {
        // Dynamic import keeps fontkit + Noto off the WinAnsi (Latin) bake path.
        const { loadOverlayUnicodeFonts, registerOverlayFontkit } = await import('./overlay_unicode_fonts');
        registerOverlayFontkit(pdfDoc);
        const bytes = await loadOverlayUnicodeFonts({
            cjk: unencodable.some((line) => [...line].some(overlayCharNeedsCjk)),
            notoSans: unencodable.some((line) => [...line].some((ch) => overlayCharNeedsNotoSans(ch, helvetica))),
        });
        unicode = {
            ...(bytes.regular ? { regular: await pdfDoc.embedFont(bytes.regular, { subset: true }) } : {}),
            ...(bytes.bold ? { bold: await pdfDoc.embedFont(bytes.bold, { subset: true }) } : {}),
            ...(bytes.cjk ? { cjk: await pdfDoc.embedFont(bytes.cjk, { subset: true }) } : {}),
        };
    }
    const fonts = createStampFonts(helvetica, helveticaBold, unicode);
    const pages = pdfDoc.getPages();
    for (const field of signatures) {
        const pageIndex = field.page - 1;
        if (pageIndex < 0 || pageIndex >= pages.length) {
            continue;
        }
        drawSignatureOverlay(pages[pageIndex], field, fonts, locale);
    }
    for (const field of dates) {
        const pageIndex = field.page - 1;
        if (pageIndex < 0 || pageIndex >= pages.length) {
            continue;
        }
        drawEnteredDateOverlay(pages[pageIndex], field, fonts.regular, locale);
    }
    for (const field of texts) {
        const pageIndex = field.page - 1;
        if (pageIndex < 0 || pageIndex >= pages.length) {
            continue;
        }
        drawEnteredTextOverlay(pages[pageIndex], field, fonts.regular);
    }
    for (const field of choices) {
        const pageIndex = field.page - 1;
        if (pageIndex < 0 || pageIndex >= pages.length) {
            continue;
        }
        drawSelectedChoiceOverlay(pages[pageIndex], field);
    }
    return pdfDoc.save();
}
export async function generateSignedPdfFile(args, fileName = 'signed_consent.pdf') {
    const bytes = await generateSignedPdf(args);
    return new File([bytes], fileName, { type: 'application/pdf' });
}
